import React, { useState } from 'react';
import { Modal, Badge } from 'react-bootstrap';
import {
  IconBook2,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
} from '@tabler/icons-react';
import '../../components/UserGuideModal.css';

// ─────────────────────────────────────────────────────────────────────────────
// SEÇÕES DO GUIA
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'what',
    title: '1. O que é o nó API?',
    content: (
      <>
        <p>
          O nó <strong>API</strong> faz uma chamada HTTP para qualquer sistema externo — CRMs, ERPs,
          agendadores, bases de dados, etc. — e pode usar a resposta para gerar uma mensagem ao cliente
          automaticamente.
        </p>
        <p>
          Diferente de um nó de <em>Mensagem com IA</em>, o nó API <strong>não precisa de nenhum nó extra</strong>:
          basta configurar o <strong>Prompt de interpretação</strong> e ele já chama a IA e responde ao cliente
          com os dados retornados.
        </p>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Fluxo mínimo funcional:</strong>{' '}
            <code>Início → API (com Prompt de interpretação) → Fim</code>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'request',
    title: '2. Configuração da requisição',
    content: (
      <>
        <table className="guide-table">
          <thead>
            <tr><th>Campo</th><th>O que faz</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>URL do Endpoint</strong></td><td>Endereço da API. Suporta variáveis: <code>{'{{variavel}}'}</code></td></tr>
            <tr><td><strong>Método HTTP</strong></td><td>GET, POST, PUT, PATCH ou DELETE</td></tr>
            <tr><td><strong>Headers (JSON)</strong></td><td>Cabeçalhos HTTP, ex: <code>{'{"Authorization": "Bearer {{token}}"}'}</code></td></tr>
            <tr><td><strong>Timeout</strong></td><td>Segundos máximos de espera (10–60s). Padrão: 30s</td></tr>
          </tbody>
        </table>

        <h4 className="mt-3">Modos de Body (POST / PUT / PATCH)</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Modo</th><th>Quando usar</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Campos visuais</strong></td>
              <td>Mapeie campos coletados do cliente para chaves da API. Simples e sem código.</td>
            </tr>
            <tr>
              <td><strong>Smart Body (IA)</strong></td>
              <td>
                Cole um exemplo do JSON que a API espera. A IA preenche automaticamente
                com os dados do cliente. Ideal para payloads complexos.
              </td>
            </tr>
            <tr>
              <td><strong>Manual</strong></td>
              <td>JSON digitado à mão com suporte a <code>{'{{variavel}}'}</code>.</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'variables',
    title: '3. Usando variáveis',
    content: (
      <>
        <p>
          Use <code>{'{{nome_da_variavel}}'}</code> em qualquer campo de texto: URL, Headers ou Body.
          O valor será substituído automaticamente pelo dado coletado do cliente.
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Exemplo</th><th>Resultado</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code>{'https://api.com/clientes/{{cpf}}'}</code></td>
              <td><code>https://api.com/clientes/12345678900</code></td>
            </tr>
            <tr>
              <td><code>{'{"telefone": "{{phone}}"}'}</code></td>
              <td><code>{'{"telefone": "47991360752"}'}</code></td>
            </tr>
            <tr>
              <td><code>{'Authorization: Bearer {{api_token}}'}</code></td>
              <td>Token real definido como variável</td>
            </tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            Ative <strong>"Enviar apenas números"</strong> para remover pontos, traços e parênteses
            de campos como CPF e telefone antes de enviar para a API.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'response',
    title: '4. Resposta da API',
    content: (
      <>
        <table className="guide-table">
          <thead>
            <tr><th>Campo</th><th>O que faz</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Salvar resposta em</strong></td>
              <td>
                Nome da variável onde os dados ficam salvos. Padrão: <code>api_response</code>.
                Use <code>{'{{api_response}}'}</code> em nós posteriores para acessar os dados.
              </td>
            </tr>
            <tr>
              <td><strong>Prompt de interpretação</strong></td>
              <td>
                Instrução para a IA interpretar os dados recebidos e responder ao cliente.
                Quando preenchido, o nó API <strong>chama o LLM automaticamente</strong> e
                gera a resposta — sem precisar de um nó extra de "Mensagem com IA".
              </td>
            </tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Exemplo de Prompt de interpretação:</strong><br />
            <em>"Liste os horários disponíveis de forma clara e amigável, destacando o mais próximo."</em><br />
            <em>"Informe ao cliente o status do pedido e a previsão de entrega."</em>
          </div>
        </div>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            Quando há um nó de <strong>Mensagem com IA</strong> após o nó API, os dados também ficam
            disponíveis automaticamente no contexto desse nó — o Prompt de interpretação serve de guia
            para a IA saber como usá-los.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'limits',
    title: '5. Limites de resultados enviados à IA',
    content: (
      <>
        <p>
          Quando a API retorna uma <strong>lista de itens</strong>, o sistema limita automaticamente
          a quantidade enviada ao modelo de IA para evitar erros de contexto e respostas lentas.
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Configuração</th><th>Valor padrão</th><th>Como alterar</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Máximo de itens (listas)</strong></td>
              <td>50 itens</td>
              <td>Campo <strong>"Máximo de itens enviados à IA"</strong> na configuração do nó</td>
            </tr>
            <tr>
              <td><strong>Tamanho máximo de strings</strong></td>
              <td>500 caracteres por campo</td>
              <td>Automático — campos maiores são truncados</td>
            </tr>
          </tbody>
        </table>
        <p>
          Quando a lista é truncada, um objeto de metadados é adicionado ao final indicando
          o total real de itens:
        </p>
        <div className="guide-code">
          {`[ item1, item2, ..., item50,\n  { "_truncated": true, "_total_items": 1000, "_items_shown": 50 } ]`}
        </div>
        <p>
          A IA recebe esse objeto e sabe que existem mais itens do que os exibidos,
          podendo informar o cliente corretamente.
        </p>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>API retornando muitos resultados?</strong><br />
            Se a sua API retorna centenas de itens e você precisa que a IA analise todos,
            considere filtrar os dados no lado da API (ex: enviar parâmetros de busca na URL)
            antes de chegarem ao agente.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'errors',
    title: '6. Tratamento de erros',
    content: (
      <>
        <p>
          Configure a <strong>Mensagem de erro (fallback)</strong> para o que o agente deve dizer
          ao cliente caso a API retorne um erro HTTP ou fique indisponível.
        </p>
        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            Se a mensagem de erro não for configurada, o agente usará uma mensagem genérica:
            <em>"Não foi possível consultar o sistema externo."</em>
          </div>
        </div>
        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Dica:</strong> Use uma mensagem de fallback amigável e ofereça uma alternativa:
            <em>"No momento não consigo consultar o sistema. Posso te ajudar de outra forma?"</em>
          </div>
        </div>
      </>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ACCORDION SECTION
// ─────────────────────────────────────────────────────────────────────────────

const GuideSection = ({ section, isOpen, onToggle }) => (
  <div className={`guide-section ${isOpen ? 'guide-section--open' : ''}`}>
    <button className="guide-section__header" onClick={onToggle}>
      <span className="guide-section__title">{section.title}</span>
      <span style={{ fontSize: 18, lineHeight: 1 }}>{isOpen ? '▾' : '▸'}</span>
    </button>
    {isOpen && (
      <div className="guide-section__content">
        {section.content}
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ApiCallGuideModal = () => {
  const [show, setShow] = useState(false);
  const [openSections, setOpenSections] = useState(['what']);

  const toggleSection = (id) =>
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  return (
    <>
      <Badge
        bg=""
        onClick={() => setShow(true)}
        style={{
          cursor: 'pointer',
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          color: '#fff',
          fontSize: '0.7rem',
          fontWeight: 600,
          padding: '4px 8px',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginLeft: '8px',
          verticalAlign: 'middle',
        }}
      >
        <IconBook2 size={13} stroke={2} />
        Guia
      </Badge>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        centered
        scrollable
        className="user-guide-modal"
      >
        <Modal.Header closeButton className="user-guide-modal__header">
          <Modal.Title className="user-guide-modal__title">
            <div className="user-guide-modal__title-icon">
              <IconBook2 size={20} />
            </div>
            Guia — Nó API (Chamada HTTP)
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="user-guide-modal__body">
          <p className="user-guide-modal__intro">
            Como configurar e usar o nó de chamada a APIs externas. Clique em cada seção para expandir.
          </p>

          <div className="user-guide-modal__controls">
            <button
              className="user-guide-modal__control-btn"
              onClick={() => setOpenSections(SECTIONS.map((s) => s.id))}
            >
              Expandir tudo
            </button>
            <button
              className="user-guide-modal__control-btn"
              onClick={() => setOpenSections([])}
            >
              Recolher tudo
            </button>
          </div>

          <div className="guide-sections">
            {SECTIONS.map((section) => (
              <GuideSection
                key={section.id}
                section={section}
                isOpen={openSections.includes(section.id)}
                onToggle={() => toggleSection(section.id)}
              />
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer className="user-guide-modal__footer">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShow(false)}
          >
            Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApiCallGuideModal;
